import React, { useEffect, useState, lazy } from 'react';
import { useParams, Link } from 'react-router-dom';
import Apiservice from '../services/Apiservice';
import CommonFunctions from '../components/CommonFunctions';
import { RenderNavigation, RenderFooter, RenderRightAside } from '../components/ReusableFragments';

const Slider = lazy(() => import('react-slick'));

const MatchDetails = () => {
    const { id, wplmatch } = useParams();

    const [tournamentId] = useState('3');
    const [battingInnings1, setBattingInnings1] = useState([]);
    const [bowlingInnings1, setBowlingInnings1] = useState([]);
    const [battingInnings2, setBattingInnings2] = useState([]);
    const [bowlingInnings2, setBowlingInnings2] = useState([]);
    const [todaySfs, setTodaySfs] = useState([]);
    const [pointsTable, setPointsTable] = useState([]);
    const [posts, setPosts] = useState([]);
    const [matchStats, setMatchStats] = useState({});
    const [teamStats, setTeamStats] = useState([]);
    const [birthdays, setBirthdays] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    const formattedMatchName = wplmatch
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');

    useEffect(() => {
        const fetchPost = async () => {
            setIsLoading(true);
             setError(null);
            try {
                const response = await Apiservice.getmatchdetails(id, tournamentId);
                setBattingInnings1(response.data.match_stats.batting_innings_1);
                setBowlingInnings1(response.data.match_stats.bowling_innings_1);
                setBattingInnings2(response.data.match_stats.batting_innings_2);
                setBowlingInnings2(response.data.match_stats.bowling_innings_2);
                setMatchStats(response.data.matchdetails);
                setTeamStats(response.data.team_stats);
                setPointsTable(response.data.total_points);
                setTodaySfs(response.data.total_csf[0]);
                setPosts(response.data.posts);
                setBirthdays(response.data.birthdays);
            } catch (error) {
                setError("Error fetching match details. Please try again.");
                console.error("Error fetching match data:", error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchPost();
    }, [id, tournamentId]);

    // Determine teams and totals
    const team1Stats = teamStats.find(team => team.team_id === matchStats.team1_id) || {};
    const team2Stats = teamStats.find(team => team.team_id === matchStats.team2_id) || {};

    // Use bowling stats to determine batting teams (bowling team in innings 1 is the opponent of batting team 1)
    const bowlingTeam1Shortcode = bowlingInnings1.length > 0 ? bowlingInnings1[0].team_shortname : matchStats.team2_shortcode;
    const bowlingTeam2Shortcode = bowlingInnings2.length > 0 ? bowlingInnings2[0].team_shortname : matchStats.team1_shortcode;
    const battingTeam1Shortcode = bowlingTeam1Shortcode === matchStats.team1_shortcode ? matchStats.team2_shortcode : matchStats.team1_shortcode;
    const battingTeam2Shortcode = bowlingTeam2Shortcode === matchStats.team2_shortcode ? matchStats.team1_shortcode : matchStats.team2_shortcode;

    // Match team_stats to innings based on shortcode
    const battingTeam1Stats = teamStats.find(team => team.team_shortcode === battingTeam1Shortcode) || team1Stats;
    const battingTeam2Stats = teamStats.find(team => team.team_shortcode === battingTeam2Shortcode) || team2Stats;

    const sliderSettings = {
        dots: true,
        infinite: birthdays.length > 3, // Only infinite if more than slidesToShow
        speed: 500,
        slidesToShow: Math.min(birthdays.length || 1, 3), // Default to 1 if empty
        slidesToScroll: 1,
        autoplay: birthdays.length > 1, // Disable autoplay for single item
        autoplaySpeed: 2000,
        responsive: [
            { breakpoint: 768, settings: { slidesToShow: Math.min(birthdays.length || 1, 2), slidesToScroll: 1 } },
            { breakpoint: 480, settings: { slidesToShow: 1, slidesToScroll: 1 } }
        ]
    };

    const renderBirthdays = () => {
        if (birthdays.length === 0) {
            return <div className='text-center text-white'>No birthdays today!</div>;
        }

        if (birthdays.length <= 2) {
            // Static layout for 1-2 items
            return (
                <div className='d-flex justify-content-center flex-wrap'>
                    {birthdays.map((item, index) => (
                        <div className='m-2' key={index}>
                            <div className='d-flex justify-content-center mb-2'>
                                <img
                                    src={`images/players/${item.first_name.toLowerCase()}-${item.last_name.toLowerCase()}.webp`}
                                    alt={`${item.first_name} ${item.last_name}`}
                                    style={{ width: '160px' }}
                                    onError={(e) => { e.target.onerror = null; e.target.src = 'images/players/default.webp'; }}
                                    loading="lazy"
                                />
                            </div>
                            <div className='text-center text-white'>
                                <h4 className='fs-5 m-0'>{item.first_name} {item.last_name}</h4>
                                <small>{item.country || 'N/A'}</small><br />
                                <small>{CommonFunctions.getPostDateFormat2(item.date_of_birth)}</small>
                            </div>
                        </div>
                    ))}
                </div>
            );
        }

        // Slider for 3+ items
        return (
            <Slider {...sliderSettings}>
                {birthdays.map((item, index) => (
                    <div key={index}>
                        <div className='d-flex justify-content-center mb-2'>
                            <img
                                src={`images/players/${item.first_name.toLowerCase()}-${item.last_name.toLowerCase()}.webp`}
                                alt={`${item.first_name} ${item.last_name}`}
                                style={{ width: '160px' }}
                                onError={(e) => { e.target.onerror = null; e.target.src = 'images/players/default.webp'; }}
                                loading="lazy"
                            />
                        </div>
                        <div className='text-center text-white'>
                            <h4 className='fs-5 m-0'>{item.first_name} {item.last_name}</h4>
                            <small>{item.country || 'N/A'}</small><br />
                            <small>{CommonFunctions.getPostDateFormat2(item.date_of_birth)}</small>
                        </div>
                    </div>
                ))}
            </Slider>
        );
    };    
      
    const renderLoaderOrError = () => (
        <>
          {isLoading && <p>Loading match details...</p>}
          {error && <p className="text-danger">Error: {error}</p>}
        </>
      );

    return (
        <React.Fragment>
            <div className="bg-fluid"></div>
            <RenderNavigation />
            <div className='container-fluid'>
                {renderLoaderOrError()}
                <nav aria-label="breadcrumb" className=''>
                    <ol className="breadcrumb">
                        <li className="breadcrumb-item"><Link to="/" className="text-muted text-decoration-none">Home</Link></li>
                        <li className="breadcrumb-item text-muted">{(matchStats.match_no === '21' ? 'Eliminator' : matchStats.match_no === '22' ? 'Finals' : matchStats.match_no)} Match</li>
                        <li className="breadcrumb-item text-white active" aria-current="page">{formattedMatchName}</li>
                    </ol>
                </nav>
                <div className='row'>
                    <div className='col-md-9'>
                        <main>
                            <section>
                                <div className="d-flex bg-white shadow mb-4 rounded">
                                    <div className="d-flex flex-shrink-0 align-items-center">
                                        <img src={`images/logos/${matchStats.team1_shortcode?.toLowerCase()}-sm_wplcricinfo.jpg`} style={{ width: '55px' }} alt={matchStats.team1_shortcode} />
                                        <span className='mx-2'>vs</span>
                                        <img src={`images/logos/${matchStats.team2_shortcode?.toLowerCase()}-sm_wplcricinfo.jpg`} style={{ width: '55px' }} alt={matchStats.team2_shortcode} />
                                    </div>
                                    <div className="flex-grow-1 ms-3">
                                        <span>
                                            <span className="badge rounded-pill text-bg-primary">{(matchStats.match_no === '21' ? 'Eliminator' : matchStats.match_no === '22' ? 'Finals' : matchStats.match_no)} Match</span>
                                            <small>{matchStats.team1_shortcode} vs {matchStats.team2_shortcode}</small>
                                            <small> ({matchStats.playing_time}) {CommonFunctions.getDateFormat(matchStats.match_date)}, {CommonFunctions.getFormattedTime(matchStats.match_time)}</small>
                                        </span>
                                        <h2 className='my-1 fs-5'>{matchStats.team1_name} vs {matchStats.team2_name}</h2>
                                        <p className=''>{matchStats.venue_name}, {matchStats.city}, {matchStats.venue_country}</p>
                                    </div>
                                </div>

                                <div className="alert alert-primary shadow py-0 d-flex align-items-center justify-content-between" role="alert">
                                    <span className='text-uppercase'>
                                        <i className='bi bi-trophy text-dark me-2'></i>
                                        {(matchStats.winner_id === matchStats.team1_id) ? matchStats.team1_name : matchStats.team2_name}
                                    </span>
                                    <span className='float-end'>
                                        Player of the match:
                                        <img
                                            src={`images/players/${matchStats.pom_player_name?.toLowerCase().replace(/\s+/g, '-')}.webp`}
                                            alt={matchStats.pom_player_name}
                                            title={matchStats.pom_player_name}
                                            className='rounded'
                                            style={{ width: '65px' }}
                                            onError={(e) => { e.target.onerror = null; e.target.src = 'images/players/default.webp'; }}
                                            loading="lazy"
                                        />
                                    </span>
                                </div>

                                <ul className="nav nav-pills bg-white" id="pills-tab" role="tablist">
                                    <li className="nav-item" role="presentation">
                                        <button className="nav-link active" id="pills-home-tab" data-bs-toggle="pill" data-bs-target="#pills-home" type="button" role="tab" aria-controls="pills-home" aria-selected="true">First Innings</button>
                                    </li>
                                    <li className="nav-item" role="presentation">
                                        <button className="nav-link" id="pills-profile-tab" data-bs-toggle="pill" data-bs-target="#pills-profile" type="button" role="tab" aria-controls="pills-profile" aria-selected="false">Second Innings</button>
                                    </li>
                                </ul>
                                <div className="tab-content" id="pills-tabContent">
                                    <div className="tab-pane fade show active" id="pills-home" role="tabpanel" aria-labelledby="pills-home-tab" tabIndex="0">
                                        <div className='card shadow'>
                                            <div className='card-body p-1 m-0'>
                                                <table className="table table-bordered table-sm m-0">
                                                    <thead>
                                                        <tr>
                                                            <th colSpan={6} valign='middle'>{battingTeam1Shortcode} Batting Innings</th>
                                                        </tr>
                                                        <tr>
                                                            <th>Player Name</th>
                                                            <th></th>
                                                            <th>Runs</th>
                                                            <th>Balls</th>
                                                            <th>Fours</th>
                                                            <th>Sixes</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="table-group-divider">
                                                        {battingInnings1.map((items, index) => (
                                                            <tr key={index}>
                                                                <td>{items.player_name}</td>
                                                                <td>
                                                                    {(items.out_type === null && items.runs === null) ? (
                                                                        <React.Fragment></React.Fragment>
                                                                    ) : items.out_type === 'caught' ? (
                                                                        <React.Fragment><span className='me-1'>c</span>{items.fielder_name}<span className="mx-1">b</span>{items.bowler_name}</React.Fragment>
                                                                    ) : items.out_type === 'bowled' ? (
                                                                        <React.Fragment><span className="mx-1">b</span>{items.bowler_name}</React.Fragment>
                                                                    ) : items.out_type === 'lbw' ? (
                                                                        <React.Fragment><span className="mx-1">lbw</span>{items.bowler_name}</React.Fragment>
                                                                    ) : items.out_type === 'runout' ? (
                                                                        <React.Fragment><span className="mx-1">runout ({items.fielder_name})</span>{items.bowler_name}</React.Fragment>
                                                                    ) : items.out_type === 'stumped' ? (
                                                                        <React.Fragment><span className="mx-1">st ({items.fielder_name})</span>{items.bowler_name}</React.Fragment>
                                                                    ) : (
                                                                        <React.Fragment>not out</React.Fragment>
                                                                    )}
                                                                </td>
                                                                <td>{items.runs}</td>
                                                                <td>{items.runs !== null ? items.balls_faced : ''}</td>
                                                                <td>{items.fours}</td>
                                                                <td>{items.sixes}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                    <tfoot>
                                                        <tr>
                                                            <td colSpan={1}><strong>Total</strong></td>
                                                            <td><strong>Total Extras: </strong>{battingTeam1Stats.total_extras}</td>
                                                            <td colSpan={4}>
                                                                <strong>
                                                                    {battingTeam1Stats.total_scored_runs}/{battingTeam1Stats.total_out_count}
                                                                </strong>
                                                                <span className='ms-2'>
                                                                    Overs <strong>({battingTeam1Stats.total_overs} /20)</strong>
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    </tfoot>
                                                </table>
                                            </div>
                                        </div>
                                        <div className='card my-2 shadow'>
                                            <div className='card-body p-0'>
                                                <table className="table table-bordered table-sm m-0">
                                                    <thead>
                                                        <tr>
                                                            <th colSpan={11} valign='middle'>{bowlingTeam1Shortcode} Bowling Innings</th>
                                                        </tr>
                                                        <tr>
                                                            <th>Player Name</th>
                                                            <th>Overs</th>
                                                            <th>Maidens</th>
                                                            <th>Dots</th>
                                                            <th>Runs</th>
                                                            <th>Wickets</th>
                                                            <th>Economy</th>
                                                            <th>Fours</th>
                                                            <th>Sixes</th>
                                                            <th>Nb</th>
                                                            <th>Wides</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="table-group-divider">
                                                        {bowlingInnings1.map((items, index) => (
                                                            <tr key={index}>
                                                                <td>{items.player_name}</td>
                                                                <td>{parseFloat(items.overs).toFixed(1)}</td>
                                                                <td>{items.maidens}</td>
                                                                <td>{items.dots_bowled}</td>
                                                                <td>{items.runs_given}</td>
                                                                <td>{items.wickets}</td>
                                                                <td>{items.economy_rate}</td>
                                                                <td>{items.fours_given}</td>
                                                                <td>{items.sixes_given}</td>
                                                                <td>{items.nb_bowled}</td>
                                                                <td>{items.wd_bowled}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="tab-pane fade" id="pills-profile" role="tabpanel" aria-labelledby="pills-profile-tab" tabIndex="0">
                                        <div className='card shadow'>
                                            <div className='card-body p-1 m-0'>
                                                <table className="table table-bordered table-sm m-0">
                                                    <thead>
                                                        <tr>
                                                            <th colSpan={6} valign='middle'>{battingTeam2Shortcode} Batting Innings</th>
                                                        </tr>
                                                        <tr>
                                                            <th>Player Name</th>
                                                            <th></th>
                                                            <th>Runs</th>
                                                            <th>Balls</th>
                                                            <th>Fours</th>
                                                            <th>Sixes</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="table-group-divider">
                                                        {battingInnings2.map((items, index) => (
                                                            <tr key={index}>
                                                                <td>{items.player_name}</td>
                                                                <td>
                                                                    {(items.out_type === null && items.runs === null) ? (
                                                                        <React.Fragment></React.Fragment>
                                                                    ) : items.out_type === 'caught' ? (
                                                                        <React.Fragment><span className='me-1'>c</span>{items.fielder_name}<span className="mx-1">b</span>{items.bowler_name}</React.Fragment>
                                                                    ) : items.out_type === 'bowled' ? (
                                                                        <React.Fragment><span className="mx-1">b</span>{items.bowler_name}</React.Fragment>
                                                                    ) : items.out_type === 'lbw' ? (
                                                                        <React.Fragment><span className="mx-1">lbw</span>{items.bowler_name}</React.Fragment>
                                                                    ) : items.out_type === 'runout' ? (
                                                                        <React.Fragment><span className="mx-1">runout ({items.fielder_name})</span>{items.bowler_name}</React.Fragment>
                                                                    ) : items.out_type === 'stumped' ? (
                                                                        <React.Fragment><span className="mx-1">st ({items.fielder_name})</span>{items.bowler_name}</React.Fragment>
                                                                    ) : (
                                                                        <React.Fragment>not out</React.Fragment>
                                                                    )}
                                                                </td>
                                                                <td>{items.runs}</td>
                                                                <td>{items.balls_faced !== 0 ? items.balls_faced : ''}</td>
                                                                <td>{items.fours}</td>
                                                                <td>{items.sixes}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                    <tfoot>
                                                        <tr>
                                                            <td colSpan={1}><strong>Total</strong></td>
                                                            <td><strong>Total Extras: </strong>{battingTeam2Stats.total_extras}</td>
                                                            <td colSpan={4}>
                                                                <strong>
                                                                    {battingTeam2Stats.total_scored_runs}/{battingTeam2Stats.total_out_count}
                                                                </strong>
                                                                <span className='ms-2'>
                                                                    Overs <strong>({battingTeam2Stats.total_overs} /20)</strong>
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    </tfoot>
                                                </table>
                                            </div>
                                        </div>
                                        <div className='card my-2 shadow'>
                                            <div className='card-body p-0'>
                                                <table className="table table-bordered table-sm m-0">
                                                    <thead>
                                                        <tr>
                                                            <th colSpan={11} valign='middle'>{bowlingTeam2Shortcode} Bowling Innings</th>
                                                        </tr>
                                                        <tr>
                                                            <th>Player Name</th>
                                                            <th>Overs</th>
                                                            <th>Maidens</th>
                                                            <th>Dots</th>
                                                            <th>Runs</th>
                                                            <th>Wickets</th>
                                                            <th>Economy</th>
                                                            <th>Fours</th>
                                                            <th>Sixes</th>
                                                            <th>Nb</th>
                                                            <th>Wides</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="table-group-divider">
                                                        {bowlingInnings2.map((items, index) => (
                                                            <tr key={index}>
                                                                <td>{items.player_name}</td>
                                                                <td>{parseFloat(items.overs).toFixed(1)}</td>
                                                                <td>{items.maidens}</td>
                                                                <td>{items.dots_bowled}</td>
                                                                <td>{items.runs_given}</td>
                                                                <td>{items.wickets}</td>
                                                                <td>{items.economy_rate}</td>
                                                                <td>{items.fours_given}</td>
                                                                <td>{items.sixes_given}</td>
                                                                <td>{items.nb_bowled}</td>
                                                                <td>{items.wd_bowled}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </section>
                            <section>
                                <div className='row'>
                                    <div className='col-md-12'>
                                        <div className='card shadow'>
                                            <div className='card-body p-1 m-0'>
                                                <table className="table table-bordered table-sm m-0">
                                                    <thead>
                                                        <tr><th colspan="2">Match  No: {matchStats.match_no}, {matchStats.team1_shortcode} vs {matchStats.team2_shortcode} Details:</th></tr>
                                                    </thead>
                                                    <tbody>
                                                        <tr>
                                                            <td>Match Result</td>
                                                            <td>{matchStats.match_summary}</td>
                                                        </tr>
                                                        <tr>
                                                            <td>Teams</td>
                                                            <td>{matchStats.team1_name} vs {matchStats.team2_name}</td>
                                                        </tr>
                                                        <tr>
                                                            <td>Date</td>
                                                            <td>{CommonFunctions.getPostDateFormat(matchStats.match_date)}, {CommonFunctions.getFormattedTime(matchStats.match_time)}, D/{matchStats.playing_time}</td>
                                                        </tr>
                                                        <tr>
                                                            <td>Venue</td>
                                                            <td>{matchStats.venue_name}, {matchStats.city}, {matchStats.venue_country}</td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </section>
                        </main>
                    </div>
                    <div className="col-md-3">
                        <RenderRightAside todaySfs={todaySfs} pointsTable={pointsTable} playerBdays={birthdays}  posts={posts}/>
                    </div>
                </div>
            </div>
            <RenderFooter />
        </React.Fragment>
    );
};

export default MatchDetails;